import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AppState} from "lib/redux/store";
import {AlgoliaWine} from "./winePickerModel";
import {wineAppApi} from "lib/api/wineAppApi";
import {SearchIndex} from "algoliasearch";
import {isEmpty} from "ramda";
import {useAppSelector} from "../../lib/redux/hooks";

export type CategoryType = 'Rotwein' | 'Weisswein' | 'Schaumwein' | 'Rosé' | 'Süsswein' | null
type PickedWines = { [id: string]: AlgoliaWine }

export interface FavoriteWines {
    customer: string
    wines: AlgoliaWine[]
}

interface WinePickerState {
    pickedWines: PickedWines
    searchTerm: string
    category: CategoryType
    favoriteWines: FavoriteWines | null
    favoriteWinesRequestPending: boolean
    favoriteWinesRequestError: string | null
    isFavoriteWinesShowed: boolean
}

const initialState: WinePickerState = {
    pickedWines: {},
    searchTerm: "",
    category: null,
    favoriteWines: null,
    favoriteWinesRequestPending: false,
    favoriteWinesRequestError: null,
    isFavoriteWinesShowed: false,
};

interface FetchFavoriteWinesRequest {
    userId: string
    algoliaProductIndex: SearchIndex // there is probably a better way to get a reference to the index...
}

const fetchFavoriteWines = createAsyncThunk<AlgoliaWine[], FetchFavoriteWinesRequest>(
    'winePicker/fetchFavoriteWines',
    async (request: FetchFavoriteWinesRequest) => {
        const response = await wineAppApi.fetchFrequentlyBoughtWines({soldTo: request.userId});
        if (isEmpty(response)) {
            return Promise.resolve([]);
        }
        const algoliaIds = response.map(i => i.id);
        const algoliaResponse = await request.algoliaProductIndex.getObjects(algoliaIds);
        return (algoliaResponse.results as AlgoliaWine[]).filter(v=>!!v);
    }
);

const {actions, reducer} = createSlice({
    name: 'winePicker',
    initialState,
    reducers: {
        setWinePicked: (state, action: PayloadAction<{ wine: AlgoliaWine, selected: boolean }>) => {
            if (action.payload.selected) {
                state.pickedWines[action.payload.wine.objectID] = action.payload.wine;
            } else {
                delete state.pickedWines[action.payload.wine.objectID];
            }
        },
        unpickAll: state => {
            state.pickedWines = {};
        },
        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload;

            if (state.isFavoriteWinesShowed) {
                state.category = null;
                state.isFavoriteWinesShowed = false;
            }
        },
        showFavoriteWines: state => {
            state.isFavoriteWinesShowed = true;
            state.category = null;
        },
        setCategory: (state, action: PayloadAction<CategoryType>) => {
            state.isFavoriteWinesShowed = false;
            state.category = action.payload;
        },
        reset: state => {
            state.pickedWines = {};
            state.searchTerm = "";
            state.category = null;
            state.isFavoriteWinesShowed = false;
        }
    },
    extraReducers: builder => {
        builder.addCase(fetchFavoriteWines.pending, state => {
            state.favoriteWinesRequestPending = true;
            state.favoriteWinesRequestError = null;
            state.isFavoriteWinesShowed = false;
        });

        builder.addCase(fetchFavoriteWines.rejected, (state, action) => {
            state.favoriteWinesRequestPending = false;
            state.favoriteWinesRequestError = action.error.message ?? null;
            state.favoriteWines = null;
        });

        builder.addCase(fetchFavoriteWines.fulfilled, (state, action) => {
            state.favoriteWinesRequestPending = false;
            state.favoriteWinesRequestError = null;
            state.favoriteWines = {
                customer: action.meta.arg.userId,
                wines: action.payload
            }
        });
    }
});

function getWinePickerState(state: AppState): WinePickerState {
    return state.winePicker;
}

const selectors = {
    pickedWines: (state: AppState) => getWinePickerState(state).pickedWines,
    searchTerm: (state: AppState) => getWinePickerState(state).searchTerm,
    category: (state: AppState) => getWinePickerState(state).category,
    favoriteWines: (state: AppState) => getWinePickerState(state).favoriteWines,
    favoriteWinesAvailable: (state: AppState) => {
        const favoriteWines = selectors.favoriteWines(state);
        return favoriteWines != null && !isEmpty(favoriteWines.wines)
    },
    favoriteWineRequestPending: (state: AppState) => getWinePickerState(state).favoriteWinesRequestPending,
    favoriteWineRequestError: (state: AppState) => getWinePickerState(state).favoriteWinesRequestError,
    isFavoriteWinesShowed: (state: AppState) => getWinePickerState(state).isFavoriteWinesShowed
};

export function useIsWinePicked(): (wine: AlgoliaWine) => boolean {
    const pickedWines = useAppSelector(selectors.pickedWines);

    return wine => {
        return wine.objectID in pickedWines
    };
}

const publicActions = {
    setWinePicked: actions.setWinePicked,
    unpickAll: actions.unpickAll,
    setSearchTerm: actions.setSearchTerm,
    setCategory: actions.setCategory,
    showFavoriteWines: actions.showFavoriteWines,
    reset: actions.reset,
    fetchFavoriteWines: fetchFavoriteWines
};

export {selectors as winePickerSelectors}
export {reducer as winePickerReducer};
export {publicActions as winePickerActions};
